// Achievement badges. Most conditions derive from data we already persist
// (streaks, archive, days played). A few are "moment-in-time" (a no-mistake
// cipher, a sub-3 guess, a fast typing run) that can't be reconstructed from
// the archive — those are read from TODAY's progress snapshot and, once seen,
// stored permanently in settings.earnedBadges so they stick.
import { readArchive, readAllGameStreaks, readProgressSnapshot, type GameKey } from './progress';
import { readSettings, writeSettings } from './settings';

export type Badge = {
	id: string;
	name: string;
	icon: string; // emoji — fallback when no image is set
	image?: string; // optional SVG art under static/, preferred over the emoji when present
	description: string; // shown as the unlock hint
	category: 'Milestones' | 'Streaks' | 'Mastery' | 'Social';
};

// Display order = catalog order.
export const BADGES: Badge[] = [
	{ id: 'first-steps', name: 'First Steps', icon: '🎯', image: '/badges/first_steps.svg', description: 'Finish your first puzzle.', category: 'Milestones' },
	{ id: 'regular', name: 'Regular', icon: '📅', image: '/badges/regular.svg', description: 'Play on 7 different days.', category: 'Milestones' },
	{ id: 'devoted', name: 'Devoted', icon: '🗓️', image: '/badges/devoted.svg', description: 'Play on 30 different days.', category: 'Milestones' },
	{ id: 'centurion', name: 'Centurion', icon: '💯', image: '/badges/centurion.svg', description: 'Play on 100 different days.', category: 'Milestones' },
	{ id: 'perfect-day', name: 'Perfect Day', icon: '⭐', image: '/badges/perfect_day.svg', description: 'Complete every game in a single day.', category: 'Milestones' },
	{ id: 'flawless-week', name: 'Flawless Week', icon: '🏆', image: '/badges/flawless_week.svg', description: 'Rack up 7 perfect days.', category: 'Milestones' },
	{ id: 'explorer', name: 'Explorer', icon: '🧭', image: '/badges/explorer.svg', description: 'Play every game at least once.', category: 'Milestones' },

	{ id: 'on-fire', name: 'On Fire', icon: '🔥', image: '/badges/on_fire.svg', description: 'Reach a 7-day streak in any game.', category: 'Streaks' },
	{ id: 'inferno', name: 'Inferno', icon: '🌋', image: '/badges/inferno.svg', description: 'Reach a 30-day streak in any game.', category: 'Streaks' },

	{ id: 'ace', name: 'Ace', icon: '🎲', image: '/badges/ace.svg', description: 'Solve Word Guess in 3 guesses or fewer.', category: 'Mastery' },
	{ id: 'genius', name: 'Genius', icon: '🧠', image: '/badges/genius.svg', description: 'Reach the Genius rank in Word Build.', category: 'Mastery' },
	{ id: 'cipher-cracker', name: 'Cipher Cracker', icon: '🔓', image: '/badges/cipher_cracker.svg', description: 'Solve Word Crypto with no mistakes.', category: 'Mastery' },
	{ id: 'speed-demon', name: 'Speed Demon', icon: '⚡', image: '/badges/speed_demon.svg', description: 'Hit 40 WPM in Word Speed.', category: 'Mastery' },
	{ id: 'flawless-fill', name: 'Flawless Fill', icon: '🔢', image: '/badges/flawless_fill.svg', description: 'Solve Num Fill with no mistakes.', category: 'Mastery' },
	{ id: 'tower-master', name: 'Tower Master', icon: '🏙️', image: '/badges/tower_master.svg', description: 'Make a 7-letter word in Word Tower.', category: 'Mastery' },

	{ id: 'teammate', name: 'Teammate', icon: '🤝', image: '/badges/teammate.svg', description: 'Finish a game in co-op.', category: 'Social' }
];

const SPEED_DEMON_WPM = 40;

/** Compute which badges are satisfied right now from local data. */
function evaluateSatisfied(): Set<string> {
	const out = new Set<string>();
	const archive = readArchive();
	const streaks = readAllGameStreaks();
	const snap = readProgressSnapshot();

	const totalDays = archive.length;
	if (archive.length > 0) out.add('first-steps');
	if (totalDays >= 7) out.add('regular');
	if (totalDays >= 30) out.add('devoted');
	if (totalDays >= 100) out.add('centurion');

	// Perfect day: Word Cross S and M count as one "Word Cross" (mirrors the
	// profile page's perfect-day logic so the two never disagree).
	const perfectDays = archive.filter((d) => {
		const g = d.games;
		const crossDone = g['word-cross'] || g['word-cross-m'];
		const restDone = Object.entries(g).every(([key, done]) => key === 'word-cross' || key === 'word-cross-m' || done);
		return crossDone && restDone;
	}).length;
	if (perfectDays >= 1) out.add('perfect-day');
	if (perfectDays >= 7) out.add('flawless-week');

	// Explorer: every game played at least once (cross counts if either variant).
	const everPlayed = (g: GameKey) => archive.some((d) => d.games[g]);
	const playedAll = everPlayed('word-guess') && everPlayed('word-build') && everPlayed('word-group') && everPlayed('word-find') && everPlayed('word-crypto') && everPlayed('word-speed') && everPlayed('num-fill') && everPlayed('word-tower') && (everPlayed('word-cross') || everPlayed('word-cross-m'));
	if (playedAll) out.add('explorer');

	const bestStreak = Math.max(0, ...Object.values(streaks).map((s) => s.best));
	if (bestStreak >= 7) out.add('on-fire');
	if (bestStreak >= 30) out.add('inferno');

	// Moment-in-time (today's snapshot only).
	const wg = snap['word-guess'];
	if (wg?.solved && wg.guessesUsed <= 3) out.add('ace');
	const wb = snap['word-build'];
	if (wb?.currentRank === 'Genius') out.add('genius');
	const wc = snap['word-crypto'];
	if (wc?.complete && wc.solved && wc.mistakes === 0) out.add('cipher-cracker');
	const wsp = snap['word-speed'];
	if (wsp?.complete && wsp.wpm >= SPEED_DEMON_WPM) out.add('speed-demon');
	const wf = snap['num-fill'];
	if (wf?.complete && wf.solved && wf.mistakes === 0) out.add('flawless-fill');
	const wt = snap['word-tower'];
	if (wt?.complete && wt.longestWord.length >= 7) out.add('tower-master');
	if (Object.values(snap).some((p) => p && (p as { coop?: boolean }).coop)) out.add('teammate');

	return out;
}

/**
 * Union currently-satisfied badges into the persisted set and return both the
 * full earned list and any newly-earned ids (for a celebratory toast).
 */
export function refreshEarnedBadges(): { earned: string[]; newlyEarned: string[] } {
	const settings = readSettings();
	const earned = new Set(settings.earnedBadges);
	const satisfied = evaluateSatisfied();
	const newlyEarned: string[] = [];
	for (const id of satisfied) {
		if (!earned.has(id)) {
			earned.add(id);
			newlyEarned.push(id);
		}
	}
	if (newlyEarned.length > 0) {
		writeSettings({ ...readSettings(), earnedBadges: [...earned] });
	}
	return { earned: [...earned], newlyEarned };
}

export function getBadge(id: string): Badge | undefined {
	return BADGES.find((b) => b.id === id);
}
